import React from 'react';

/**
 * The one boolean on/off switch.
 *
 * Promoted from a local component in the admin News Feeds tool when a second
 * surface (per-user feed selection in profile) needed the same control. The
 * design system had no switch primitive — `ViewToggle` is a view-mode picker,
 * and `Button` is for actions — so hand-rolled switches were the alternative,
 * which is how a second control family starts.
 *
 * A switch is not a button: it reports a state rather than firing an action,
 * so it renders `role="switch"` with `aria-checked` and always carries an
 * accessible name. `toggleConventions.test.ts` fails the build on a
 * hand-rolled `role="switch"` outside this file.
 *
 * Colour: the track uses the Pro palette by default. Citizen surfaces have
 * their own CSS-variable theme and pass `checkedStyle` rather than importing
 * Pro tokens, which is why this takes a style override instead of hardcoding
 * one app's palette.
 */
export interface ToggleSwitchProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  /** Accessible name. Say what it toggles — "Switch on Colorado Sun". */
  label: string;
  disabled?: boolean;
  /** Track colour when on, for apps outside the Pro palette. */
  checkedStyle?: React.CSSProperties;
  className?: string;
}

const ToggleSwitch: React.FC<ToggleSwitchProps> = ({
  checked,
  onChange,
  label,
  disabled = false,
  checkedStyle,
  className = '',
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    style={checked ? checkedStyle : undefined}
    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
      checked && !checkedStyle ? 'bg-primary-600' : ''
    } ${!checked ? 'bg-neutral-300 dark:bg-neutral-600' : ''} ${className}`}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
        checked ? 'translate-x-6' : 'translate-x-1'
      }`}
    />
  </button>
);

export default ToggleSwitch;
